/**
 * /admin/dna-queue — DNA Verification review queue.
 *
 * Lists every paid $75 DNA Pedigree Verification request.
 * Admin reviews uploaded panel files (PDF / images), then either
 * approves (flips dog.dna_verified=true → gold badge everywhere)
 * or requests changes (owner can re-upload, no re-charge).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { TestTubeIcon as TestTube, CheckCircleIcon as CheckCircle, ArrowCounterClockwiseIcon as ArrowCounterClockwise, XIcon as X } from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending_upload: "bg-[#FFF3CD] text-[#8A6A00] border-[#FFE58F]",
  pending_review: "bg-[#E0F2FE] text-[#075985] border-[#BAE6FD]",
  approved: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  rejected: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

const STATUS_LABEL = {
  pending_upload: "Awaiting upload",
  pending_review: "Awaiting review",
  approved: "Approved",
  rejected: "Rejected",
};

export default function AdminDNAQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending_review");
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/dna-verifications", {
        params: { status: filter },
      });
      setRows(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/dna-verifications/${id}/approve`);
      toast.success("DNA verification approved — gold badge active.");
      setSelected(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reject = async (id, force = false) => {
    if (!reason.trim()) {
      toast.error("Add a reason so the owner knows what to fix.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/dna-verifications/${id}/reject`, {
        reason: reason.trim(),
        force,
      });
      toast.success(
        force ? "Hard-rejected (owner notified)." : "Changes requested — owner can re-upload.",
      );
      setSelected(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-spectral text-3xl tracking-tight flex items-center gap-3">
              <TestTube size={32} weight="duotone" className="text-gold" />
              DNA Verification Queue
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-1">
              $75 DNA-Verified Pedigree Registrations awaiting review. Approving flips the gold
              DNA-Verified badge on the dog across every public surface.
            </p>
          </div>
          <div
            className="flex gap-2 flex-wrap sm:flex-nowrap overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0"
            data-testid="dna-queue-filter"
          >
            {["pending_review", "pending_upload", "approved", "rejected", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`dna-queue-filter-${s}`}
                className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border ${
                  filter === s
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-ink hover:text-ink"
                }`}
              >
                {s === "all" ? "All" : STATUS_LABEL[s] || s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-[#6B6B6B]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-12 text-center">
            <TestTube size={40} weight="duotone" className="text-[#6B6B6B]/40 mx-auto mb-3" />
            <p className="text-[#6B6B6B]">No verifications in this queue.</p>
          </div>
        ) : (
          <>
            {/* Mobile: stacked card list — tables horizontal-scroll on phones
                even with overflow-x, which means admins have to swipe to
                see each row's status. Cards keep all info visible. */}
            <div className="md:hidden space-y-3" data-testid="dna-queue-cards-mobile">
              {rows.map((r) => (
                <div
                  key={r.id}
                  className="bg-white border border-[#EAEAEA] rounded-xl p-4"
                  data-testid={`dna-queue-card-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <Link
                        to={`/dogs/${r.dog_id}`}
                        className="font-semibold text-ink hover:text-crimson block truncate"
                      >
                        {r.dog_name || "(unnamed)"}
                      </Link>
                      <div className="text-xs text-[#6B6B6B]">{r.dog_breed}</div>
                    </div>
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border whitespace-nowrap ${
                        STATUS_STYLE[r.status] || "bg-gray-100 border-gray-200 text-gray-700"
                      }`}
                    >
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </div>
                  <div className="text-xs text-[#6B6B6B] mb-3">
                    <div className="truncate">
                      <span className="font-semibold text-ink">{r._owner?.name || "—"}</span>
                      {r._owner?.email ? ` · ${r._owner.email}` : ""}
                    </div>
                    <div>
                      {(r.files || []).length} file{(r.files || []).length === 1 ? "" : "s"} ·
                      submitted {(r.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full min-h-[44px]"
                    onClick={() => {
                      setSelected(r);
                      setReason(r.reject_reason || "");
                    }}
                    data-testid={`dna-queue-review-mobile-${r.id}`}
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>

            {/* Desktop / tablet: table with horizontal scroll fallback */}
            <div className="hidden md:block bg-white border border-[#EAEAEA] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="dna-queue-table">
                  <thead className="bg-[#FAFAFA] border-b border-[#EAEAEA] text-left text-xs uppercase tracking-wider text-[#6B6B6B]">
                    <tr>
                      <th className="px-4 py-3">Dog</th>
                      <th className="px-4 py-3">Owner</th>
                      <th className="px-4 py-3">Files</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Submitted</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        className="border-t border-[#F1F1F1] hover:bg-[#FAFAFA]"
                        data-testid={`dna-queue-row-${r.id}`}
                      >
                        <td className="px-4 py-3">
                          <Link
                            to={`/dogs/${r.dog_id}`}
                            className="font-semibold text-ink hover:text-crimson"
                          >
                            {r.dog_name || "(unnamed)"}
                          </Link>
                          <div className="text-xs text-[#6B6B6B]">{r.dog_breed}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-ink">{r._owner?.name || "—"}</div>
                          <div className="text-xs text-[#6B6B6B]">{r._owner?.email || ""}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-semibold">{(r.files || []).length}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${
                              STATUS_STYLE[r.status] || "bg-gray-100 border-gray-200 text-gray-700"
                            }`}
                          >
                            {STATUS_LABEL[r.status] || r.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#6B6B6B]">
                          {(r.created_at || "").slice(0, 10)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelected(r);
                              setReason(r.reject_reason || "");
                            }}
                            data-testid={`dna-queue-review-${r.id}`}
                          >
                            Review
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <Sheet
        open={!!selected}
        onOpenChange={(o) => {
          if (!o) {
            setSelected(null);
            setReason("");
          }
        }}
      >
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-spectral">
                  {selected.dog_name || "Dog"} — DNA Verification
                </SheetTitle>
                <SheetDescription>
                  {selected.dog_breed} · Submitted by{" "}
                  {selected._owner?.name || selected._owner?.email || "owner"}.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-2">
                    Uploaded files ({(selected.files || []).length})
                  </div>
                  {(selected.files || []).length === 0 ? (
                    <div className="text-sm text-[#6B6B6B] italic">
                      Owner has not uploaded the DNA panel yet.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {selected.files.map((f) => (
                        <li
                          key={f.id}
                          className="flex items-center justify-between gap-3 border border-[#EAEAEA] rounded-lg px-3 py-2 bg-white"
                        >
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{f.filename}</div>
                            <div className="text-xs text-[#6B6B6B]">
                              {f.content_type} ·{" "}
                              {((f.size_bytes || 0) / 1024).toFixed(0)} KB
                            </div>
                            {f.description && (
                              <div className="text-xs text-[#6B6B6B] italic">
                                {f.description}
                              </div>
                            )}
                          </div>
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-bold uppercase tracking-wider text-crimson hover:underline whitespace-nowrap"
                            data-testid={`dna-file-open-${f.id}`}
                          >
                            Open ↗
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {(selected.review_history || []).length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B] mb-2">
                      Review history
                    </div>
                    <ul className="text-xs text-[#6B6B6B] space-y-1">
                      {selected.review_history.map((h, i) => (
                        <li key={i}>
                          {(h.at || "").slice(0, 10)} — <b>{h.action}</b>
                          {h.reason ? ` — ${h.reason}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(selected.status === "pending_review" ||
                  selected.status === "pending_upload") && (
                  <>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-[#6B6B6B]">
                        Reason (required for "Request changes" / "Reject")
                      </label>
                      <Textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Panel image is illegible. Please re-upload a higher-resolution scan of pages 1-2."
                        className="mt-1.5 min-h-[80px]"
                        data-testid="dna-review-reason"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        disabled={busy || (selected.files || []).length === 0}
                        onClick={() => approve(selected.id)}
                        className="bg-green-700 hover:bg-green-800 text-white min-h-[44px]"
                        data-testid="dna-review-approve"
                      >
                        <CheckCircle size={16} weight="fill" className="mr-1" />
                        Approve
                      </Button>
                      <Button
                        disabled={busy}
                        variant="outline"
                        onClick={() => reject(selected.id, false)}
                        className="min-h-[44px]"
                        data-testid="dna-review-request-changes"
                      >
                        <ArrowCounterClockwise size={16} className="mr-1" />
                        Request changes
                      </Button>
                      <Button
                        disabled={busy}
                        variant="outline"
                        onClick={() => reject(selected.id, true)}
                        className="border-crimson text-crimson hover:bg-crimson hover:text-white min-h-[44px]"
                        data-testid="dna-review-reject"
                      >
                        <X size={16} className="mr-1" />
                        Hard reject
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </AppShell>
  );
}
